import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Dispersals from './pages/Dispersals';
import { authService, ErrorBoundary } from '@uniqcamp/shared';
import SchoolPortalApiListener from './components/SchoolPortalApiListener';

const theme = createTheme({
  palette: {
    primary: {
      main: '#4f227d',
      light: '#7b4da8',
      dark: '#371755',
      contrastText: '#fff',
    },
    secondary: {
      main: '#ffe200',
      light: '#ffeb3d',
      dark: '#e6cb00',
      contrastText: '#1a1a1a',
    },
    background: {
      default: '#f8f6fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 44,
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          '@media (min-width: 600px)': { minHeight: 36 },
        },
      },
    },
    MuiIconButton: { styleOverrides: { root: { minWidth: 48, minHeight: 48 } } },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12, boxShadow: '0 2px 12px rgba(79, 34, 125, 0.08)' },
      },
    },
    MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
    MuiChip: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #4f227d 0%, #371755 100%)',
          boxShadow: '0 2px 12px rgba(79, 34, 125, 0.25)',
        },
      },
    },
  },
});

const PrivateRoute = ({ children }: { children: React.ReactNode }): React.ReactElement | null => {
  const isAuthenticated = authService.isAuthenticated();
  const user = authService.getStoredUser();

  if (!isAuthenticated || !user || user.role !== 'CAB_DRIVER') {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SchoolPortalApiListener />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/students"
            element={
              <PrivateRoute>
                <Students />
              </PrivateRoute>
            }
          />
          <Route
            path="/dispersals"
            element={
              <PrivateRoute>
                <Dispersals />
              </PrivateRoute>
            }
          />
        </Routes>
      </Router>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
